import { runValidationStepUsingBuiltInValidators as runBuiltInValidator } from './validationStep';
import { ValidatorRunner, ValidationDescriptor, ValidationStep } from './types';
import { isAllowedValue } from './isAllowedValue';

type ValidationAccumulator =
  | { isValid: true }
  | { isValid: false; errorMessages: string[] };

const addError = (acc: ValidationAccumulator, message?: string) => {
  if (acc.isValid === false) {
    if (!message) return acc;
    return {
      ...acc,
      errorMessages: [...acc.errorMessages, message],
    };
  }
  return {
    isValid: false,
    errorMessages: message ? [message] : [],
  };
};

const runValidationSteps = <T extends ValidationStep<any, any>>(args: {
  runValidator: ValidatorRunner<T>;
  validationSteps: T[];
  value: unknown;
  exitEarly?: boolean;
}) => {
  const { runValidator, validationSteps, value } = args;

  let statusAcc: ValidationAccumulator = { isValid: true };
  for (const validator of validationSteps) {
    const isValid = runValidator(validator, value);
    if (isValid === undefined) {
      throw new Error(`Unhandled validator: ${validator.id}`);
    }
    if (isValid === false) {
      statusAcc = addError(statusAcc, validator.message);
    }
  }

  return statusAcc;
};

export const validate = <StepT extends ValidationStep<any, any>>(
  fieldDesc: ValidationDescriptor<StepT>
): ValidationAccumulator => {
  const { value, validationSteps } = fieldDesc;

  // Check if value is part of whitelisted values (often null/undefined)
  if (isAllowedValue(fieldDesc)) {
    return { isValid: true };
  }

  return runValidationSteps({
    runValidator: runBuiltInValidator,
    validationSteps: validationSteps.flat(),
    value,
  });
};

type ValidationAccumulatorObject =
  | { isValid: true }
  | { isValid: false; errorMessages: Record<string, string[]> };

const setErrorsByKey = (
  acc: ValidationAccumulatorObject,
  payload: { key: string; errorMessages: string[] }
) => {
  const { key, errorMessages } = payload;
  if (acc.isValid) {
    return { isValid: false, errorMessages: { [key]: errorMessages } };
  }

  return {
    ...acc,
    errorMessages: { ...acc.errorMessages, [key]: errorMessages },
  };
};

export const validateAllObject = (
  fieldDescriptors: Record<string, ValidationDescriptor>
) => {
  return Object.entries(fieldDescriptors)
    .map(([fieldName, fieldDesc]) => {
      return [fieldName, validate(fieldDesc)] as const;
    })
    .reduce(
      (acc, [fieldName, validationResult]) => {
        if (validationResult.isValid) {
          return acc;
        }

        return setErrorsByKey(acc, {
          key: fieldName,
          errorMessages: validationResult.errorMessages,
        });
      },
      { isValid: true } as ValidationAccumulatorObject
    );
};
